import React from 'react';

import {ConnectorSizing, LinkerDomainVisualization, SharedDomainParams} from 'antibodies/AntibodyTypedefs';
import AntibodyPathLinker, {
  ArcDirection,
  ArcSize,
  PathParameter,
  PathParameterType,
} from 'antibodies/visualization/AntibodyPathLinker';

export type AntibodyLinkerProps = SharedDomainParams & {
  /* The color of the linker */
  color: string;
  domainVisualization: LinkerDomainVisualization;
  connectorSizing: ConnectorSizing;
};

/** The path a linker is drawn along, in the linker's own coordinate space. */
export function getLinkerPathParameters(
  domainVisualization: Pick<LinkerDomainVisualization, 'centerLength' | 'endRadius' | 'tipLength'>
): Array<PathParameter> {
  const {centerLength, endRadius, tipLength} = domainVisualization;
  return [
    // Start from the lower end of the link. Move to the start of the arc.
    {type: PathParameterType.LINE, dx: 0, dy: tipLength},
    // Move along an arc to the bottom of the center.
    {
      type: PathParameterType.ARC,
      rx: endRadius,
      ry: endRadius,
      dx: endRadius * 2,
      dy: 0,
      rotation: 0,
      arcSize: ArcSize.SMALL,
      direction: ArcDirection.COUNTER_CLOCKWISE,
    },
    // Move up along the center.
    {type: PathParameterType.LINE, dx: 0, dy: -centerLength},
    // Move along an arc to the top of the upper end.
    {
      type: PathParameterType.ARC,
      rx: endRadius,
      ry: endRadius,
      dx: endRadius * 2,
      dy: 0,
      rotation: 0,
      arcSize: ArcSize.SMALL,
      direction: ArcDirection.CLOCKWISE,
    },
    // Move to the upper end of the link.
    {type: PathParameterType.LINE, dx: 0, dy: tipLength},
  ];
}

/**
 * A long center line with a clockwise arc on each end, intended to link two `AntibodyDomain`.
 * This is an SVG component, so all units are 'abstract user units', relative to the SVG viewbox.
 * @param domainVisualization The visualization for the linker domain
 * @param connectorSizing The sizing params for this format's connectors
 * @param color The color of the line
 * @param proteinId The API ID of the protein entity associated with this domain. If passed, the linker will
 *                  be given a SmartChip-style popover with metadata about the protein.
 * @param tipLength The length of the tip after the arc
 */
export default function AntibodyLinker({
  domainVisualization,
  color,
  proteinId,
  inaccessible,
  label,
  showPopovers,
  connectorSizing,
  antibodyId,
}: AntibodyLinkerProps): JSX.Element {
  const pathParameters = getLinkerPathParameters(domainVisualization);
  return (
    <AntibodyPathLinker
      pathParameters={pathParameters}
      color={color}
      proteinId={proteinId}
      inaccessible={inaccessible}
      label={label}
      showPopovers={showPopovers}
      connectorSizing={connectorSizing}
      antibodyId={antibodyId}
    />
  );
}
